import { useEffect, useState } from 'react';

import { ApiError, type Field, type Sample, api, unwrap } from '@/api';

import { SmallMultiples } from './Charts';
import { Select, Spinner } from './ui';

const SPANS = [
	{ label: '5 minutes', ms: 5 * 60_000 },
	{ label: '1 hour', ms: 3_600_000 },
	{ label: '24 hours', ms: 86_400_000 },
	{ label: '7 days', ms: 7 * 86_400_000 },
];
const POINTS = 360;

/**
 * Offline preview of what a sensor with these parameters emits. Models are dt-aware, so a long
 * span previewed at a coarse step behaves like the real instance (diurnal cycles, drift, events).
 */
export function PreviewPanel({ definitionId, params, faults, seed }: { definitionId: string; params: unknown; faults: unknown; seed?: string }) {
	const [span, setSpan] = useState(SPANS[1]!.ms);
	const [data, setData] = useState<{ fields: Field[]; samples: Sample[] }>();
	const [error, setError] = useState<string>();
	const [busy, setBusy] = useState(false);
	const body = JSON.stringify({ definitionId, params, faults, seed, span });

	useEffect(() => {
		let cancelled = false;
		const t = setTimeout(async () => {
			setBusy(true);
			try {
				const res = await unwrap(
					api.POST('/api/v1/preview', {
						body: {
							definitionId,
							params: params as Record<string, unknown>,
							faults: faults as Record<string, unknown>,
							seed: seed || undefined,
							sampleIntervalMs: Math.max(1, Math.round(span / POINTS)),
							count: POINTS,
						},
					}),
				);
				if (!cancelled) {
					setData(res);
					setError(undefined);
				}
			} catch (e) {
				if (!cancelled) setError(e instanceof ApiError ? (e.problem.errors?.[0] ? `${e.problem.errors[0].path}: ${e.problem.errors[0].message}` : e.message) : String(e));
			} finally {
				if (!cancelled) setBusy(false);
			}
		}, 400);
		return () => {
			cancelled = true;
			clearTimeout(t);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [body]);

	return (
		<div className="flex flex-col gap-3">
			<div className="flex items-center justify-between gap-2">
				<div className="flex items-center gap-2 text-sm font-semibold">
					Preview {busy && <Spinner />}
				</div>
				<Select value={span} onChange={(e) => setSpan(Number(e.target.value))} className="w-auto">
					{SPANS.map((s) => (
						<option key={s.ms} value={s.ms}>
							{s.label}
						</option>
					))}
				</Select>
			</div>
			{error && <p className="rounded-md bg-danger-soft px-3 py-2 text-xs text-danger">{error}</p>}
			{data && <SmallMultiples fields={data.fields} samples={data.samples} height={90} />}
			<p className="text-[11px] text-fg-muted">Generated offline from the same pipeline the live sensor runs; nothing is saved.</p>
		</div>
	);
}
